import App from "../index";
import { debounce, elementHeight, elementY, getEl } from "../helpers";

export type ScrollDirection = "UP"|"DOWN";
export type ScrollCallback = (direction: ScrollDirection, progress?: number, oldProgress?:number) => any;

export interface ScrollHandler {
	onScroll: ScrollCallback,
	onEnteredView: (direction: ScrollDirection) => any,
	onExitedView: (direction: ScrollDirection) => any,
}

export interface ScrollTrigger
{
	el: WatchedElement;
	windowOffset: number;
	elementOffset: number;
	elementOffsetEnd?: number;
	handler:ScrollHandler;

	expandUpVh: number;
	expandDownVh: number;
	once: boolean;

	//Calculated on Resize and used every scroll tick.
	elTop: number;
	elBottom: number;
	elHgt: number;

	//Current progress of the scroll trigger from 0 to 1.
	progress: number,

	//True if the element is in view inside the window.
	inView: boolean,

	//Mark as deleted, will be removed on the next Cron fucnction cycle.
	deleted: boolean,
}

export interface WatchedElement
{
	el:Element;
	y: number;
	height: number;
}

export default class ScrollManager
{

	app: App;
	watchedElements: WatchedElement[] = [];
	triggers: ScrollTrigger[] = [];
	cronTimer: number = 0;
	
	debugEls: any[] = [];

	public prevScroll: number = 0;
	public scroll: number = 0;
	public pageHeight: number = 0;

	public debug: boolean = false; //Debug mode enabled/disabled - draws coloured overlays over each scroll trigger area and darkens them when active.

	constructor(app: App) {

		this.onResize = debounce(this.onResize.bind(this), 250);

		window.addEventListener("scroll", this.onScroll.bind(this));
		window.addEventListener("resize", this.onResize.bind(this));
		this.app = app;
		this.scroll = window.scrollY;
		this.prevScroll = window.scrollY;
		this.cronTimer = setInterval(this.onCron.bind(this), 10000);
	}

	watchElement(queryOrElement:string|Element):WatchedElement|false {
		if (typeof queryOrElement === "string")
		{
			queryOrElement = getEl(queryOrElement);
		}
		if (!queryOrElement) return false;

		for (let i = 0; i < this.watchedElements.length; i++)
		{
			if (this.watchedElements[i].el == queryOrElement) return this.watchedElements[i];
		}
		this.watchedElements.push({
			el: queryOrElement,
			y: elementY(queryOrElement),
			height: elementHeight(queryOrElement)
		});
		
		

		return this.watchedElements[this.watchedElements.length-1];
	}

	unwatchElement(element:Element)
	{
		for (let i = 0; i < this.watchedElements.length; i++)
		{
			if (this.watchedElements[i].el === element)
			{
				this.watchedElements.splice(i, 1);
				return;
			}
		}
	}

	/**
	 *
	 * @param elementOrQuery - The Dom Element or CSS Selector String to add a scroll trigger for.
	 * @param handler - The object which has 3 methods on it - onScroll, onEnteredView, onExitedView. The callback is given the direction (UP or DOWN) and the progress of the scroll through the element from 0 to 1.
	 * @param windowOffset - Where on the screen/window does the scroll count from? 0 = Top of the window, 1 = Bottom of the window, 0.5 = Middle of the window.
	 * @param elementOffset - Where on the element does the scroll START counting from? 0 = Top of the element, 1 = Bottom of the element, 0.5 = Middle of the element.
	 * @param elementOffsetEnd - Where on the element does the scroll FINISH counting to? 0 = Top of the element, 1 = Bottom of the element, 0.5 = Middle of the element.
	 * @param expandUpVh - How many Vh's to add to the element START point?
	 * @param expandDownVh - How many Vh's to add to the element's END point?
	 * @param once - Boolean Only trigger once?
	 */
	addScrollTrigger(elementOrQuery:string|Element,handler: ScrollHandler,  windowOffset: number = 1, elementOffset:number = 0, elementOffsetEnd: number = 1, expandUpVh:number = 0, expandDownVh: number = 0, once:boolean = false)
	{
		let watchEl = this.watchElement(elementOrQuery);
		if (!watchEl) {
			console.error("Unable to find element ", elementOrQuery, " for scroll trigger. Skipping.");
			return;
		}

		this.triggers.push({
			el: watchEl,
			handler: handler,
			windowOffset: windowOffset,
			elementOffset: elementOffset,
			elementOffsetEnd: elementOffsetEnd,
			expandDownVh: expandDownVh,
			expandUpVh: expandUpVh,
			once: once,

			elTop:0,
			elBottom:0,
			elHgt:0,
			progress: 0,
			inView: false,
			deleted: false,
		});
		
		if (this.debug)
		{
			const colors = ['red', 'green', 'blue', 'coral', 'indigo', 'dodgerblue', 'cornflowerblue'];
			const debugOverlay = document.createElement('div');
			debugOverlay.setAttribute('style', 'position:absolute;' +
				'left:0;right:0; ' +
				'top: calc('+Math.round(elementOffset*100)+'% - '+expandUpVh+'vh); ' +
				'bottom: calc('+Math.round(100-(elementOffsetEnd*100))+'% - '+expandDownVh+'vh); ' +
				'background: '+colors[this.triggers.length % colors.length]+'; ' +
				'opacity:0.15'
			);
			debugOverlay.classList.add('scroll-debug');
			watchEl.el.appendChild(debugOverlay);
			watchEl.el.classList.add('relative');
		}

		this.onResize(); //This is debounced so it should only be called a single time no matter how many scroll triggers are added at once.
	}

	addVisibilityTrigger(elementOrQuery:string|Element, handler:(dir?:ScrollDirection)=>any, windowOffset: number = 0.5)
	{
		this.addScrollTrigger(
			elementOrQuery,
			{
				onEnteredView: handler,
				onExitedView: () => {},
				onScroll: () => {}
			},
			windowOffset,
			0, 1, 0, 1, true
		);
	}

	recalculate() {
		this.pageHeight = elementHeight(document.documentElement);
		this.scroll = window.scrollY;
		this.prevScroll = window.scrollY;

		for (let i = 0; i < this.watchedElements.length; i++)
		{
			this.watchedElements[i].height = elementHeight(this.watchedElements[i].el);
			this.watchedElements[i].y = elementY(this.watchedElements[i].el);
		}


		const windowHgt = window.innerHeight;

		for (let i = 0; i < this.triggers.length; i++)
		{
			const t: ScrollTrigger = this.triggers[i];
			const elStart: number = (t.el.y + t.el.height * t.elementOffset - windowHgt * t.windowOffset);
			const elEnd: number = (t.el.y + t.el.height * t.elementOffsetEnd - windowHgt * t.windowOffset);

			this.triggers[i].elTop = elStart - t.expandUpVh * windowHgt;
			this.triggers[i].elBottom = elEnd + t.expandDownVh * windowHgt;
			this.triggers[i].elHgt = Math.max(1, Math.abs(this.triggers[i].elBottom - this.triggers[i].elTop));
			this.triggers[i].inView = false;
		}
	}

	processTriggers() {

		const scrollDelta:number = (this.scroll - this.prevScroll);
		const dir:ScrollDirection = scrollDelta < 0 ? "UP" : "DOWN";

		const windowMinY: number = Math.min(this.scroll, this.prevScroll);
		const windowMaxY: number = Math.max(this.scroll, this.prevScroll);

		for (let i = 0; i < this.triggers.length; i++)
		{
			const t:ScrollTrigger = this.triggers[i];

			if (t.deleted) continue;

			if (windowMaxY < t.elTop) {
				//Window bottom is before the top of the element - hasnt got to it yet.
				if (t.progress > 0)
				{
					t.handler.onScroll(dir, 0, t.progress);
					t.progress = 0;
				}
				if (t.inView) {
					t.inView = false;
					this.onTriggerExitedView(t, dir);
				}
				continue;
			}

			if (windowMinY > t.elBottom) {
				//Window top is after the bottom of the element - already past.
				if (t.progress < 1)
				{
					t.handler.onScroll(dir, 1, t.progress);
					t.progress = 1;
				}
				if (t.inView) {
					t.inView = false;
					this.onTriggerExitedView(t, dir);
				}
				continue;
			}

			if ((windowMinY <= t.elTop && windowMaxY >= t.elBottom))
			{
				//Window top is before top of element and window bottom is after bottom of element
				//Therefore the window is bigger than the size of the element.
				if (!t.inView) {
					
					t.inView = true;
					this.onTriggerEnteredView(t, dir);
					
					if (t.once) {
						t.deleted = true;
						continue;
					}
				}
				t.handler.onScroll(dir, 1, t.progress);
				t.progress = 1;

				continue;
			}

			let newProgress = Math.min(1, Math.max(0,(this.scroll - t.elTop) / t.elHgt));
			if (!t.inView) {
				this.onTriggerEnteredView(t, dir);
				t.inView = true;
				if (t.once)
				{
					t.deleted = true;
					continue;
				}
			}
			t.handler.onScroll(dir, newProgress, t.progress);
			t.progress = newProgress;
		}
	}

	onTriggerEnteredView(t:ScrollTrigger, dir:ScrollDirection)
	{
		if (this.debug)
		{
			const debugEl = t.el.el.querySelector('.scroll-debug') as HTMLElement;
			if (debugEl) debugEl.style.opacity = '0.25';
		}
		t.handler.onEnteredView(dir);
	}
	onTriggerExitedView(t:ScrollTrigger, dir:ScrollDirection)
	{
		if (this.debug)
		{
			const debugEl = t.el.el.querySelector('.scroll-debug') as HTMLElement;
			if (debugEl) debugEl.style.opacity = '0.1';
		}
		t.handler.onExitedView(dir);
	}
	
	
	onScroll() {
		this.prevScroll = this.scroll;
		this.scroll = window.scrollY;
		this.processTriggers();
	}

	onResize()
	{
		this.recalculate();
		this.onScroll();
	}

	onCron()
	{
		for (let i = this.triggers.length-1; i >= 0; i--)
		{
			if (this.triggers[i].deleted)
			{
				if (this.debug)
				{
					const debugEl = this.triggers[i].el.el.querySelector('.scroll-debug');
					if (debugEl) debugEl.remove();
				}
				
				this.triggers.splice(i, 1);
			}
		}

		for (let i = this.watchedElements.length-1; i >= 0; i--)
		{
			let found: boolean = false;
			for (let j = 0; j < this.triggers.length; j++)
			{
				if (this.triggers[j].el == this.watchedElements[i])
				{
					found = true;
					break;
				}
			}

			if (!found)
			{
				this.unwatchElement(this.watchedElements[i].el);
			}
		}
	}

}
